import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { registerLocaleData } from '@angular/common';
import { BrowserModule } from '@angular/platform-browser';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  CUSTOM_ELEMENTS_SCHEMA,
  ErrorHandler,
  LOCALE_ID,
  NgModule,
} from '@angular/core';

import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { AppComponent } from './app.component';
import { APP_RESOLVER_PROVIDERS } from './app.resolver';
import { AppState } from './app.service';

import { LanguageSelectorComponent } from './lib/components/languageSelector/languageSelector.component';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  HttpClientModule,
} from '@angular/common/http';
import { WelcomeComponent } from './lib/components/welcome/welcome.component';
import { SnackBarComponent } from './lib/components/snackbar/snackbar.component';
import {
  AuthGuard,
  FacebookService,
  FinnService,
  IntercomService,
  JobService,
  PaymentService,
  RequestService,
  UnitRoomService,
  UnitService,
  UserService,
  Utils,
  WebSocketService,
} from './lib/services';
import { NotificationStreamService } from './lib/services/notificationStreamService';

import { InviteComponent } from './components/invite/invite.component';
import { AngularFontAwesomeModule } from 'angular-font-awesome';
import { SelectUnitComponent } from './components/select-unit/selectUnit.component';
import { AutosizeModule } from 'ngx-autosize';

import { NgxUploaderModule } from 'ngx-uploader';

import { TranslationService } from './lib/services/translationService';
import { ContentService } from './lib/services/contentService';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';

import { FlexLayoutModule } from '@angular/flex-layout';

import { DsmComponent } from './components/dsm/dsm.component';
import { CalendarModule, DateAdapter } from 'angular-calendar';
import { adapterFactory } from 'angular-calendar/date-adapters/date-fns';
import { MjDatePickerUtilitiesService } from './shared-module/components/mj-date-picker/services/mj-date-picker-utilities.service';
import localeNb from '@angular/common/locales/nb';

import { LoadingPopupStore } from './lib/stores/loadingPopupStore/loadingPopupStore';
import { QuillModule } from 'ngx-quill';

import { ColorPickerModule } from 'ngx-color-picker';
import { MatIconModule } from '@angular/material/icon';
import { BlogService } from './lib/services/blogService';
import { Ng2ImgMaxModule } from 'ng2-img-max';

import { AppRoutingModule } from './app-routing.module';
import { CoreModule } from './core/core.module';
import { StoreModule } from '@ngrx/store';
import { environment } from '../environments/environment';
import { EffectsModule } from '@ngrx/effects';
import { metaReducers, reducers } from './reducers';
import { LeafletModule } from '@asymmetrik/ngx-leaflet';
import { AnalyticsModule } from './analytics-module/analytics.module';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { SharedModule } from './shared-module/shared.module';
import { UserJobComponent } from './components/job/userjob.component';
import { NoContentComponent } from './components/noContent/noContent.component';
import { ModalService } from './lib/services/modal.service';
import { SelectPlanComponent } from './billing-module/components/select-plan/select-plan.component';
import { BillingService } from './lib/services/billingService';
import { PaymentGuard } from './lib/services/paymentGuard';
import { UnpaidComponent } from './billing-module/components/unpaid/unpaid.component';
import { BillingUnpaidComponent } from './billing-module/components/billing-unpaid/billing-unpaid.component';
import { NgxStripeModule } from 'ngx-stripe';
import { HttpConfigInterceptor } from './lib/services/httpConfigInterceptor';
import { SettingsModule } from './settings-module/settings.module';
import { UnitAddUsersComponent } from './settings-module/components/unit-add-users/unit-add-users.component';
import { UnitAddSubUnitComponent } from './settings-module/components/unit-add-sub-unit/unit-add-sub-unit.component';
import { UnitInvitationComponent } from './components/unit-invitation/unit-invitation.component';
import { ImageCropperModule } from 'ngx-image-cropper';
import { DownloadAppComponent } from './dashboard-module/components/download-app/download-app.component';
import { DownloadAppService } from './lib/services/download-app.service';
import { IntroductionComponent } from './core/introduction/introduction.component';
import { JobsModule } from './jobs-module/jobs-module';
import { SentryErrorHandler } from './lib/services/sentry';
import { UploadImageModalComponent } from './components/upload-image-modal/upload-image-modal.component';
import { ImageService } from './lib/services/imageService';
import { NavigationStore } from './lib/stores/navigationStore/navigationStore';
import { BlogModule } from './blog-module/blog.module';
import { JobPostCanDeactivateGuard } from './lib/services/routeGuard';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

// Locales
registerLocaleData(localeNb, 'no');
registerLocaleData(localeNb, 'nb');
registerLocaleData(localeNb, 'nn');

// Application wide providers
const APP_PROVIDERS = [...APP_RESOLVER_PROVIDERS, AppState];

// AoT requires an exported function for factories
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/languages/', '-lang.json');
}

/**
 * `AppModule` is the main entry point into Angular2's bootstraping process
 */
@NgModule({
  bootstrap: [AppComponent],
  declarations: [
    // modals
    SelectPlanComponent,
    UnpaidComponent,
    BillingUnpaidComponent,
    DownloadAppComponent,
    IntroductionComponent,

    AppComponent,
    NoContentComponent,

    // App component components
    InviteComponent,
    SelectUnitComponent,

    // Language selector
    LanguageSelectorComponent,

    // Small components
    WelcomeComponent,
    SnackBarComponent,

    // Styling components
    DsmComponent,
    UserJobComponent,
    UnitAddUsersComponent,
    UnitAddSubUnitComponent,
    UnitInvitationComponent,
    UploadImageModalComponent,
  ],
  imports: [
    SharedModule,
    MatButtonModule,
    MatCheckboxModule,
    MatChipsModule,
    MatSelectModule,
    MatCardModule,
    MatGridListModule,
    MatFormFieldModule,
    MatInputModule,
    MatSlideToggleModule,
    MatMenuModule,
    MatSnackBarModule,
    MatTooltipModule,
    MatProgressBarModule,
    MatTabsModule,
    MatRadioModule,

    NgxChartsModule,

    FlexLayoutModule,

    AutosizeModule,
    BrowserModule,
    BrowserAnimationsModule,
    FormsModule,

    ReactiveFormsModule,

    AngularFontAwesomeModule,
    NgxUploaderModule,

    AppRoutingModule,
    ImageCropperModule,

    HttpClientModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    CalendarModule.forRoot({
      provide: DateAdapter,
      useFactory: adapterFactory,
    }),
    ColorPickerModule,
    QuillModule.forRoot(),
    MatIconModule,
    Ng2ImgMaxModule,
    /**
     * StoreModule.forRoot is imported once in the root module, accepting a reducer
     * function or object map of reducer functions. If passed an object of
     * reducers, combineReducers will be run creating your application
     * meta-reducer. This returns all providers for an @ngrx/store
     * based application.
     */
    StoreModule.forRoot(reducers, { metaReducers }),

    /**
     * @ngrx/router-store keeps router state up-to-date in the store.
     */

    EffectsModule.forRoot([]),

    /**
     * Store devtools instrument the store retaining past versions of state
     * and recalculating new states. This enables powerful time-travel
     * debugging.
     *
     * To use the debugger, install the Redux Devtools extension for either
     * Chrome or Firefox
     *
     * See: https://github.com/zalmoxisus/redux-devtools-extension
     */
    !environment.production
      ? StoreDevtoolsModule.instrument({
          maxAge: 25,
        })
      : [],
    CoreModule.forRoot(),
    LeafletModule.forRoot(),
    AnalyticsModule,
    SettingsModule,
    JobsModule,
    BlogModule,

    NgxStripeModule.forRoot(
      environment.production
        ? 'pk_live_5uWXjxp7ejs6W9dk85uqU5WC'
        : 'pk_test_LiyHBC0Se59KKkGyAhjCW9ka'
    ),
  ],
  /**
   * Expose our Services and Providers into Angular's dependency injection.
   */
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpConfigInterceptor,
      multi: true,
    },
    // ENV_PROVIDERS,
    APP_PROVIDERS,

    // Custom services
    TranslationService,
    ContentService,

    FacebookService,
    UnitService,
    JobService,
    BlogService,
    PaymentService,
    NotificationStreamService,
    RequestService,
    UnitRoomService,
    UserService,
    IntercomService,
    FinnService,
    PaymentGuard,
    JobPostCanDeactivateGuard,
    Utils,
    WebSocketService,
    DownloadAppService,
    MjDatePickerUtilitiesService,
    AuthGuard,
    // Stores
    NavigationStore,
    LoadingPopupStore,
    // Third party applications
    BillingService,
    ModalService,
    ImageService,
    { provide: LOCALE_ID, useValue: 'en-US' },
    // { provide: APP_BASE_HREF, useValue: '/' },
    { provide: MatDialogRef, useValue: {} },
    { provide: MAT_DIALOG_DATA, useValue: {} },
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  entryComponents: [
    SelectPlanComponent,
    UnpaidComponent,
    BillingUnpaidComponent,
    UnitAddUsersComponent,
    UnitAddSubUnitComponent,
    DownloadAppComponent,
    IntroductionComponent,
    UploadImageModalComponent,
  ],
})
export class AppModule {}
